import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import usePunishmentState, { IPunishment } from '@/states/punishment.state';
import useTenantState from '@/states/tenant.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useRefetchCompletionStatus } from './useCompletionStatus';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const usePunishmentLogic = () => {
  const formControl = useForm<IPunishment>({
    defaultValues: {
      severity_level: '',
      definition_warning: '',
      sanction_description: '',
      sanctions: [{ warning_clause: '', fee: '' }],
      is_published: false,
    },
  });
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms debounce
  const [allPublished, setAllPublished] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IPunishment | null>(null);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const { currentTenant } = useTenantState();
  const {
    punishments,
    paginatedData,
    getPunishments,
    createPunishment,
    updatePunishment,
    deletePunishment,
    bulkPublishPunishment,
    isLoading,
  } = usePunishmentState();

  // Fetch data with server-side pagination and filters
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      let published: boolean | undefined;
      if (selectedStatus === 'Published') published = true;
      else if (selectedStatus === 'Unpublished') published = false;

      await getPunishments(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        order: pageConfig.order,
        order_by: pageConfig.orderBy,
        category: selectedCategory || undefined,
        severity_level: selectedSeverity || undefined,
        published,
        search: debouncedSearchTerm || undefined,
      });
    } catch {
      toast.error('Failed to fetch punishments');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    selectedCategory,
    selectedSeverity,
    selectedStatus,
    debouncedSearchTerm,
    getPunishments,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (punishments.length > 0) {
      setAllPublished(punishments.every(item => item.is_published === true));
    } else {
      setAllPublished(false);
    }
  }, [punishments]);

  const handleSave = async (data: IPunishment) => {
    if (!currentTenant?.id) return;
    try {
      const payload: Partial<IPunishment> = {
        severity_level: data.severity_level,
        definition_warning: data.definition_warning,
        sanction_description: data.sanction_description,
        sanctions: data.sanctions,
        is_published: data.is_published ?? false,
      };

      if (formMode === 'edit' && selectedRow?.id) {
        await updatePunishment(currentTenant.id, selectedRow.id, payload);
      } else {
        await createPunishment(currentTenant.id, payload);
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({
        severity_level: '',
        definition_warning: '',
        sanction_description: '',
        sanctions: [{ warning_clause: '', fee: '' }],
        is_published: false,
      });
      await getPunishments(currentTenant.id);
      await refetchCompletionStatus();
    } catch {
      toast.error('Failed to save punishment');
    }
  };

  const handleDelete = async (row: IPunishment) => {
    if (!currentTenant?.id || !row.id) return;
    await deletePunishment(currentTenant.id, row.id);
    await getPunishments(currentTenant.id);
    await refetchCompletionStatus();
  };

  const bulkPublishPunishmentHandler = async (value: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishPunishment(currentTenant.id, value);
    await getPunishments(currentTenant.id);
  };

  const onEdit = (row: IPunishment) => {
    reset({
      severity_level: row.severity_level || '',
      definition_warning: row.definition_warning || '',
      sanction_description: row.sanction_description || '',
      sanctions: row.sanctions?.length ? row.sanctions : [{ warning_clause: '', fee: '' }],
      is_published: row.is_published ?? false,
    });

    const existingPunishment = punishments.find(item => item.severity_level === row.severity_level);

    setFormMode(existingPunishment ? 'edit' : 'add');

    setSelectedRow(existingPunishment || row);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSeverity('');
    setSelectedStatus('');
  };

  return {
    formControl,
    formMode,
    formRef,
    handleSave: handleSubmit(handleSave),
    handleDelete,
    onEdit,
    reset,
    isLoading,
    allPublished,
    paginatedData,
    pageConfig,
    setPageConfig,
    bulkPublishPunishmentHandler,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    selectedSeverity,
    setSelectedSeverity,
    selectedStatus,
    setSelectedStatus,
    clearFilters,
    setFormMode,
    fetchData,
  };
};
